import { byIdsResultJSONZ, type ByIdsResultJSON } from '_shared/_api/by-ids/by-ids-result';
import PaginatedResponse, { type Paginated } from '_shared/_api/pagination/responses/paginated-response';
import { api } from '_shared/_stores/api';
import { logSchemaFailure } from '_shared/_utilities/log-schema-failure';
import Guid from '_shared/guids/guid';
import UserInformationSchema, { UserInformationJSON } from 'users/information/_schemas/user-information-schema';
import type NetworkConnectionQuery from 'users/networking/connections/_api/queries/network-connection-query';
import NetworkConnectionForumSchema, { NetworkConnectionForumJSON } from 'users/networking/connections/_schemas/network-connection-forum-schema';
import z from 'zod';

/**
 * How the network connection slice keeps the page for an infinite list. `refresh` is page zero,
 * put in front of what is held for the same search; `next` goes after it. A request without
 * `pages` (a table page, or a read of the whole list) is not kept as a page by the slice at all, so
 * it cannot replace a list's pages with rows of another page size.
 */
export type NetworkConnectionPages = "refresh" | "next";

export type NetworkConnectionListArgs = {
  query: NetworkConnectionQuery;
  pages?: NetworkConnectionPages;
};

type NetworkConnectionTag = { type: "UserNetworkConnection"; id: string };

export function getNetworkConnectionListTags(result?: Paginated<UserInformationJSON>): NetworkConnectionTag[] {
  return [
    ...(result?.data.map(({ id }) => ({ type: "UserNetworkConnection" as const, id })) ?? []),
    { type: "UserNetworkConnection", id: "LIST" },
  ];
}

/** The LIST tag refreshes the answer when a new connection arrives, because it may be one of these ids. */
export function getNetworkConnectionsByUserIdsTags(userIds: Guid[]): NetworkConnectionTag[] {
  return [
    ...userIds.map((userId) => ({ type: "UserNetworkConnection" as const, id: userId.value })),
    { type: "UserNetworkConnection", id: "LIST" },
  ];
}

export const userNetworkConnectionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getUserNetworkConnections: (
      build.query<Paginated<UserInformationJSON>, NetworkConnectionListArgs>({
        query: ({ query }) => ({
          url: 'users/network-connections',
          params: query.searchParams,
        }),
        rawResponseSchema: z.array(UserInformationSchema.UserInformationJSONZ),
        onSchemaFailure: logSchemaFailure,
        transformResponse: (response: UserInformationJSON[], meta): Paginated<UserInformationJSON> => ({
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get('x-pagination') ?? ''),
        }),
        providesTags: getNetworkConnectionListTags,
      })
    ),
    getUserNetworkConnectionsByUserIds: (
      build.query<ByIdsResultJSON<UserInformationJSON>, { userIds: Guid[] }>({
        query: ({ userIds }) => ({
          url: 'users/network-connections/by-user-ids',
          method: 'POST',
          body: { userIds: userIds.map((userId) => userId.value) },
        }),
        rawResponseSchema: byIdsResultJSONZ(UserInformationSchema.UserInformationJSONZ),
        onSchemaFailure: logSchemaFailure,
        providesTags: (result, error, { userIds }) => getNetworkConnectionsByUserIdsTags(userIds),
      })
    ),
    getChatForumForNetworkConnection: (
      build.query<NetworkConnectionForumJSON, { userId: Guid }>({
        query: ({ userId }) => ({
          url: `users/network-chat/${userId.value}`
        }),
        rawResponseSchema: NetworkConnectionForumSchema.NetworkConnectionForumJSONZ.nullish(),
        onSchemaFailure: logSchemaFailure,
        providesTags: (result, error, id) => [{
          type: 'UserNetworkConnection',
          id: id.userId.value
        }],
      })
    ),
    // #endregion

    // #region Delete
    removeNetworkConnection: (
      build.mutation<void, { connectionId: Guid }>({
        query: ({ connectionId }) => ({
          url: `users/network-connections/${connectionId.value}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, id) => [
          {
            type: 'UserNetworkConnection',
            id: id.connectionId.value
          },
          {
            type: 'UserNetworkConnection',
            id: 'LIST'
          }
        ]
      })
    ),
    // #endregion

    // #region Create
    createChatForumForNetworkConnection: (
      build.mutation<NetworkConnectionForumJSON, { connectionId: Guid }>({
        query: ({ connectionId }) => ({
          url: `users/network-chat/${connectionId.value}/create`,
          method: 'POST'
        }),
        rawResponseSchema: NetworkConnectionForumSchema.NetworkConnectionForumJSONZ.nullish(),
        onSchemaFailure: logSchemaFailure,
        invalidatesTags: [{
          type: 'UserNetworkConnection',
          id: 'LIST'
        }]
      })
    ),
    // #endregion
  })
});

export const {
  useGetUserNetworkConnectionsQuery,
  useGetUserNetworkConnectionsByUserIdsQuery,
  useGetChatForumForNetworkConnectionQuery,
  useRemoveNetworkConnectionMutation,
  useCreateChatForumForNetworkConnectionMutation
} = userNetworkConnectionsApi;
